// audio.ts — Tone.js setup and the instrument palette.
//
// Each instrument is a Tone synth/sampler that exposes a `trigger(time, step)` method
// the sequencer calls on each step tick. The palette is keyed by InstrumentId; tracks
// reference instruments by id, not by object, so loop patterns are JSON-serializable.

import * as Tone from "tone";

export type InstrumentId = "kick" | "snare" | "hat" | "bass" | "lead" | "pad";

export interface Instrument {
  id: InstrumentId;
  label: string;
  color: string; // hex, e.g. "#ff5577"
  trigger(time: number, velocity?: number): void;
}

// Per-instrument color palette (used by track tiles + visuals)
export const INSTRUMENT_COLORS: Record<InstrumentId, string> = {
  kick:  "#ff5577",
  snare: "#ffaa44",
  hat:   "#ffee66",
  bass:  "#66ddaa",
  lead:  "#5577ff",
  pad:   "#bb77ff",
};

// Mix bus — every instrument routes through this so we can do master limiting + room volume.
let mixBus: Tone.Channel | null = null;

function ensureMixBus(): Tone.Channel {
  if (!mixBus) {
    mixBus = new Tone.Channel(-6).toDestination();
    // soft limiter so peaks don't clip when many tracks are active
    const limiter = new Tone.Limiter(-1);
    mixBus.disconnect();
    mixBus.connect(limiter);
    limiter.toDestination();
  }
  return mixBus;
}

function buildPalette(): Map<InstrumentId, Instrument> {
  const bus = ensureMixBus();
  const palette = new Map<InstrumentId, Instrument>();

  // Kick — punchy MembraneSynth
  const kick = new Tone.MembraneSynth({
    pitchDecay: 0.04,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.1 },
  }).connect(bus);
  palette.set("kick", {
    id: "kick", label: "kick", color: INSTRUMENT_COLORS.kick,
    trigger: (t, v = 1) => kick.triggerAttackRelease("C1", "8n", t, v),
  });

  // Snare — NoiseSynth
  const snare = new Tone.NoiseSynth({
    noise: { type: "white" },
    envelope: { attack: 0.001, decay: 0.18, sustain: 0 },
  }).connect(bus);
  palette.set("snare", {
    id: "snare", label: "snare", color: INSTRUMENT_COLORS.snare,
    trigger: (t, v = 0.7) => snare.triggerAttackRelease("8n", t, v),
  });

  // Hat — high-passed NoiseSynth
  const hatFilter = new Tone.Filter(7000, "highpass").connect(bus);
  const hat = new Tone.NoiseSynth({
    noise: { type: "white" },
    envelope: { attack: 0.001, decay: 0.05, sustain: 0 },
  }).connect(hatFilter);
  palette.set("hat", {
    id: "hat", label: "hat", color: INSTRUMENT_COLORS.hat,
    trigger: (t, v = 0.5) => hat.triggerAttackRelease("32n", t, v),
  });

  // Bass — MonoSynth, plays a fixed root for MVP (step pattern drives rhythm only)
  const bass = new Tone.MonoSynth({
    oscillator: { type: "sawtooth" },
    envelope: { attack: 0.01, decay: 0.2, sustain: 0.3, release: 0.2 },
    filterEnvelope: { attack: 0.01, decay: 0.1, sustain: 0.2, release: 0.5, baseFrequency: 200, octaves: 2.5 },
  }).connect(bus);
  palette.set("bass", {
    id: "bass", label: "bass", color: INSTRUMENT_COLORS.bass,
    trigger: (t, v = 0.7) => bass.triggerAttackRelease("A1", "8n", t, v),
  });

  // Lead — PolySynth
  const lead = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: "triangle" },
    envelope: { attack: 0.02, decay: 0.1, sustain: 0.4, release: 0.4 },
  }).connect(bus);
  palette.set("lead", {
    id: "lead", label: "lead", color: INSTRUMENT_COLORS.lead,
    trigger: (t, v = 0.6) => lead.triggerAttackRelease("E4", "16n", t, v),
  });

  // Pad — slow attack PolySynth on a chord
  const pad = new Tone.PolySynth(Tone.Synth, {
    oscillator: { type: "fatsine", count: 3, spread: 30 },
    envelope: { attack: 0.5, decay: 0.5, sustain: 0.8, release: 1.2 },
  }).connect(bus);
  palette.set("pad", {
    id: "pad", label: "pad", color: INSTRUMENT_COLORS.pad,
    trigger: (t, v = 0.4) => pad.triggerAttackRelease(["A3", "C4", "E4"], "2n", t, v),
  });

  return palette;
}

let _palette: Map<InstrumentId, Instrument> | null = null;

export function getPalette(): Map<InstrumentId, Instrument> {
  if (!_palette) _palette = buildPalette();
  return _palette;
}

export async function startAudio(): Promise<void> {
  // iOS Safari plays Web Audio through the "ambient" session by default, which the
  // hardware mute/ring switch silences (Android ignores it → that's why Android had
  // sound and iPhone didn't). Setting the session to "playback" plays regardless.
  try {
    const ns = navigator as unknown as { audioSession?: { type: string } };
    if (ns.audioSession) ns.audioSession.type = "playback";
  } catch {
    /* not supported — older iOS */
  }
  await Tone.start();
  // Slightly larger scheduler look-ahead trades a touch of latency for far fewer
  // buffer underruns (the crackle) when many voices fire during a drag.
  Tone.getContext().lookAhead = 0.12;
  // Build the (cheap) drum palette synchronously. Voices + reverb are warmed up
  // OFF the critical path — never awaited here, or the unlocking gesture freezes
  // and produces no sound until the convolution reverb finishes generating.
  getPalette();
  scheduleWarmUp();
}

let warmedUp = false;
function scheduleWarmUp(): void {
  if (warmedUp) return;
  warmedUp = true;
  const idle = (cb: () => void): void => {
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
    if (ric) ric(cb);
    else setTimeout(cb, 0);
  };
  idle(() => void warmUpPlink());
}

export function isAudioReady(): boolean {
  return Tone.getContext().state === "running";
}

export function setMasterVolumeDb(db: number): void {
  ensureMixBus().volume.rampTo(db, 0.1);
}

// -----------------------------------------------------------------------------
// Plink melodic voices (plink-style note canvas — see plink.ts).
//
// Pitch comes from the vertical tap position, mapped onto a C-major pentatonic
// scale so every combination of notes from every player stays consonant. Each
// user is assigned one timbre + one hue by hashing their id, so you can tell
// players apart by both sound and the colour of their ripples.
// -----------------------------------------------------------------------------

// C-major pentatonic, low → high. Top of screen = end of this array (highest).
const PLINK_SCALE = [
  "C3", "D3", "E3", "G3", "A3",
  "C4", "D4", "E4", "G4", "A4",
  "C5", "D5", "E5", "G5", "A5",
  "C6", "D6", "E6", "G6", "A6",
] as const;

/** Map a normalised vertical position (0 = top) to a scale note. Higher = higher. */
export function noteForY(y: number): string {
  const t = Math.min(1, Math.max(0, y));
  const idx = Math.round((1 - t) * (PLINK_SCALE.length - 1));
  return PLINK_SCALE[idx] ?? "C4";
}

export type PlinkVoiceId =
  | "bell" | "pluck" | "glass" | "marimba" | "wide" | "soft"
  | "bass" | "lead" | "harp" | "choir";
export const PLINK_VOICES: PlinkVoiceId[] = [
  "bell", "pluck", "glass", "marimba", "wide", "soft",
  "bass", "lead", "harp", "choir",
];
export const PLINK_VOICE_LABELS: Record<PlinkVoiceId, string> = {
  bell: "bell",
  pluck: "pluck",
  glass: "glass",
  marimba: "marimba",
  wide: "warm",
  soft: "soft",
  bass: "bass",
  lead: "lead",
  harp: "harp",
  choir: "choir",
};

// Each instrument owns a colour family; per-user shade variation is layered on
// top (see hueForUserVoice) so you can read both the instrument and the player.
const VOICE_HUE: Record<PlinkVoiceId, number> = {
  bell: 45,     // gold
  pluck: 175,   // teal
  glass: 195,   // cyan
  marimba: 30,  // amber
  wide: 280,    // violet
  soft: 330,    // rose
  bass: 255,    // indigo
  lead: 12,     // red-orange
  harp: 95,     // lime
  choir: 210,   // blue
};

export function voiceBaseHue(voice: PlinkVoiceId): number {
  return VOICE_HUE[voice];
}

/** Per-user shade offset within an instrument's colour family, −18..+18. */
function shadeForUser(userId: string): number {
  return (hashStr(userId, 53) % 37) - 18;
}

/** Hybrid colour: instrument family hue, nudged by a stable per-user shade. */
export function hueForUserVoice(userId: string, voice: PlinkVoiceId): number {
  return ((voiceBaseHue(voice) + shadeForUser(userId)) % 360 + 360) % 360;
}

/** Runtime guard for voice ids arriving over the wire. */
export function isPlinkVoice(v: unknown): v is PlinkVoiceId {
  return typeof v === "string" && (PLINK_VOICES as string[]).includes(v);
}

function hashStr(s: string, seed: number): number {
  let h = seed;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(h);
}

export function voiceForUser(userId: string): PlinkVoiceId {
  return PLINK_VOICES[hashStr(userId, 7) % PLINK_VOICES.length] ?? "bell";
}

/** A stable, vivid hue (0–359) for this user's ripples. */
export function hueForUser(userId: string): number {
  return hashStr(userId, 131) % 360;
}

let plinkReverb: Tone.Reverb | null = null;
function ensurePlinkReverb(): Tone.Reverb {
  if (!plinkReverb) {
    // Tighter reverb — long washy tails smeared the notes and piled up voices.
    plinkReverb = new Tone.Reverb({ decay: 1.8, wet: 0.22, preDelay: 0.01 }).connect(ensureMixBus());
  }
  return plinkReverb;
}

// Cap simultaneous voices per instrument so dragging can't flood the CPU and
// cause buffer underruns (the crackle/dropouts). Shorter releases free voices
// quickly so the cap is rarely hit.
const MAX_POLYPHONY = 12;

const plinkVoiceCache = new Map<PlinkVoiceId, Tone.PolySynth<any>>();

// Each voice uses a deliberately DIFFERENT synthesis engine + register so they
// don't blur together: metallic bell, FM twang, AM pad, mono saw/square,
// dual-oscillator choir, etc. `vol` balances the engines (Metal/Duo run hot).
function makeVoice(id: PlinkVoiceId): Tone.PolySynth<any> {
  const rev = ensurePlinkReverb();
  let synth: Tone.PolySynth<any>;
  let vol = -11;
  switch (id) {
    case "pluck": // twangy short FM pluck (koto-ish)
      synth = new Tone.PolySynth(Tone.FMSynth, {
        harmonicity: 1, modulationIndex: 8, oscillator: { type: "triangle" },
        envelope: { attack: 0.002, decay: 0.3, sustain: 0, release: 0.4 },
        modulation: { type: "square" },
        modulationEnvelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 },
      });
      vol = -12;
      break;
    case "glass": // bright glassy FM
      synth = new Tone.PolySynth(Tone.FMSynth, {
        harmonicity: 5, modulationIndex: 12, oscillator: { type: "sine" },
        envelope: { attack: 0.004, decay: 0.4, sustain: 0, release: 0.8 },
        modulation: { type: "sine" },
        modulationEnvelope: { attack: 0.01, decay: 0.2, sustain: 0, release: 0.3 },
      });
      vol = -15;
      break;
    case "marimba": // short woody sine mallet
      synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "sine" },
        envelope: { attack: 0.001, decay: 0.28, sustain: 0, release: 0.35 },
      });
      vol = -7;
      break;
    case "wide": // warm AM pad
      synth = new Tone.PolySynth(Tone.AMSynth, {
        harmonicity: 2, oscillator: { type: "triangle" },
        envelope: { attack: 0.08, decay: 0.3, sustain: 0.4, release: 1.4 },
      });
      vol = -13;
      break;
    case "soft": // breathy sine pad
      synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "sine" },
        envelope: { attack: 0.06, decay: 0.4, sustain: 0.3, release: 1.6 },
      });
      vol = -10;
      break;
    case "bass": // fat square sub, an octave down, low-pass swept
      synth = new Tone.PolySynth(Tone.MonoSynth, {
        oscillator: { type: "square" }, detune: -1200,
        envelope: { attack: 0.01, decay: 0.2, sustain: 0.4, release: 0.4 },
        filter: { Q: 1, type: "lowpass" },
        filterEnvelope: { attack: 0.01, decay: 0.15, sustain: 0.3, release: 0.3, baseFrequency: 120, octaves: 2.5 },
      });
      vol = -9;
      break;
    case "lead": // buzzy saw lead with filter movement
      synth = new Tone.PolySynth(Tone.MonoSynth, {
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.4 },
        filter: { Q: 2, type: "lowpass" },
        filterEnvelope: { attack: 0.02, decay: 0.2, sustain: 0.3, release: 0.4, baseFrequency: 500, octaves: 3 },
      });
      vol = -16;
      break;
    case "harp": // tight triangle pluck, an octave up
      synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "triangle" }, detune: 1200,
        envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0.6 },
      });
      vol = -9;
      break;
    case "choir": // dual-oscillator, vibrato — vocal-ish
      synth = new Tone.PolySynth(Tone.DuoSynth, {
        harmonicity: 1.5, vibratoAmount: 0.3, vibratoRate: 5,
        voice0: { oscillator: { type: "sine" }, envelope: { attack: 0.1, decay: 0.2, sustain: 0.6, release: 1.2 } },
        voice1: { oscillator: { type: "sine" }, envelope: { attack: 0.1, decay: 0.2, sustain: 0.6, release: 1.2 } },
      });
      vol = -20;
      break;
    case "bell": // metallic struck bell / chime
    default:
      synth = new Tone.PolySynth(Tone.MetalSynth, {
        harmonicity: 12, modulationIndex: 18, resonance: 800, octaves: 1.4,
        envelope: { attack: 0.001, decay: 0.5, release: 0.2 },
      });
      vol = -26;
      break;
  }
  synth.maxPolyphony = MAX_POLYPHONY;
  synth.volume.value = vol;
  // Dry path straight to the mix bus → always audible immediately, even before
  // the convolution reverb has finished generating its impulse response.
  synth.connect(ensureMixBus());
  // Parallel reverb send for ambience; contributes once its IR is ready.
  synth.connect(rev);
  return synth;
}

function getPlinkVoice(id: PlinkVoiceId): Tone.PolySynth<any> {
  let v = plinkVoiceCache.get(id);
  if (!v) {
    v = makeVoice(id);
    plinkVoiceCache.set(id, v);
  }
  return v;
}

/** Build every voice + the reverb ahead of time so switching instruments mid-jam
 *  doesn't hitch. Staggered with yields so it never blocks the main thread for
 *  long (constructing 10 polyphonic synths at once caused a visible freeze). */
export async function warmUpPlink(): Promise<void> {
  ensurePlinkReverb();
  for (const v of PLINK_VOICES) {
    getPlinkVoice(v);
    await new Promise((r) => setTimeout(r, 0)); // let the event loop breathe
  }
  // NB: do NOT await plinkReverb.ready here — voices have a dry path to the mix
  // bus, so they're audible immediately; the reverb send joins when it's ready.
}

// PolySynth throws "start time must be strictly greater than previous start
// time" if two notes are scheduled at the identical time — which then breaks all
// further audio (the silence bug). Fast drawing quantises several notes onto the
// same 16th-note grid time, so we force each voice's trigger times to strictly
// increase, and guard the trigger so one bad note can't kill the stream.
const lastTriggerTime = new Map<PlinkVoiceId, number>();

/** Play a plink note. `time` lets the caller quantise to the transport grid. */
export function triggerPlink(voice: PlinkVoiceId, note: string, time?: number, velocity = 0.85): void {
  const synth = getPlinkVoice(voice);
  let t = time ?? Tone.now();
  const prev = lastTriggerTime.get(voice) ?? 0;
  if (t <= prev) t = prev + 0.021;
  lastTriggerTime.set(voice, t);
  try {
    synth.triggerAttackRelease(note, "8n", t, velocity);
  } catch {
    /* a single dropped note must never break the audio stream */
  }
}
